// Components/RecentlyVisitedIcon.js
import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Badge,
  Divider,
  Tooltip
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ClearAll, AccessTime, Close } from '@mui/icons-material';
import { clearRecentItems, removeRecentItem ,selectRecentItems} from '../redux/recentlyVisitedSlice';


const RecentlyVisitedIcon = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const recentItems = useSelector(selectRecentItems);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (path) => {
    navigate(path);
    handleClose();
  };

  const handleClearAll = () => {
    dispatch(clearRecentItems());
    handleClose();
  };

  const handleRemoveItem = (e, path) => {
    e.stopPropagation();
    dispatch(removeRecentItem(path));
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const visitedTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - visitedTime) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="recently visited"
        sx={{
          position: 'relative',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <Badge
          badgeContent={recentItems.length}
          color="secondary"
          max={9}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.6rem',
              height: '16px',
              minWidth: '16px',
            }
          }}
        >
          <HistoryIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            mt: 1,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon fontSize="small" />
            Recently Visited
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {recentItems.length} items
            </Typography>
          </Typography>
        </Box>

        <Divider />

        {recentItems.length === 0 ? (
          <MenuItem disabled sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No recent visits
              </Typography>
            </Box>
          </MenuItem>
        ) : (
          <Box>
            {recentItems.map((item, index) => (
              <MenuItem
                key={index}
                onClick={() => handleItemClick(item.path)}
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
                sx={{

                  py: 1.5,
                  px: 2,

                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(99, 91, 255, 0.08)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <AccessTime fontSize="small" sx={{ mr: 1.5, color: 'text.secondary', minWidth: '24px' }} />
                  <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: '#333'
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.5
                      }}
                    >
                      {getTimeAgo(item.timestamp)}
                    </Typography>
                  </Box>

                  <Tooltip title="Remove from history">
                    <IconButton
                      size="small"
                      onClick={(e) => handleRemoveItem(e, item.path)}
                      sx={{
                        opacity: hoveredItem === item.path ? 1 : 0.5,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          color: '#f44336'
                        }
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </MenuItem>
            ))}

            <Divider />
            <Box sx={{ px: 1, py: 1 }}>
              <MenuItem
                onClick={handleClearAll}
                sx={{
                  justifyContent: 'center',
                  py: 1,
                  borderRadius: 1,
                  color: '#f44336',
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  }
                }}
              >
                <ClearAll fontSize="small" sx={{ mr: 1.5 }} />
                <Typography variant="body2" fontWeight={500}>
                  Clear All History
                </Typography>
              </MenuItem>
            </Box>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default RecentlyVisitedIcon;